/**
 * Minimap — Top-down view of terrain around the player.
 * Toggle with M key. Shows colored blocks, player arrow, and markers.
 */

const MAP_SIZE = 140;   // pixels
const MAP_RADIUS = 40;  // blocks radius shown
const PIXEL_SIZE = MAP_SIZE / (MAP_RADIUS * 2);

// Block color lookup (simplified)
const BLOCK_MAP_COLORS = {
  0: null,           // AIR
  1: '#4A8F29',      // GRASS
  2: '#8B6B3E',      // DIRT
  3: '#808080',      // STONE
  4: '#D4C47A',      // SAND
  5: '#6B4226',      // WOOD
  6: '#2D6B1E',      // LEAVES
  7: '#3366AA',      // WATER
  8: '#333333',      // BEDROCK
  9: '#E8E8E8',      // SNOW_GRASS
  10: '#C4AA6A',     // SANDSTONE
  11: '#2D6B2D',     // CACTUS
  12: '#B89050',     // PLANKS
  13: '#777777',     // COBBLESTONE
  14: '#AADDFF',     // GLASS
  15: '#D4AA30',     // WHEAT
  46: '#888877',     // GRAVEL
  47: '#AAAAAA',     // CLAY
  50: '#F0F0F0',     // SNOW
  51: '#BBDDFF',     // ICE
  54: '#F0F0F0',     // WOOL_WHITE
  55: '#668866',     // MOSS_STONE
  56: '#882222',     // NETHERRACK
  57: '#CCAA44',     // GLOWSTONE
  58: '#554433',     // SOUL_SAND
  59: '#442233',     // NETHER_BRICKS
  60: '#DDDDAA',     // END_STONE
  61: '#CC3333',     // BED
  62: '#5A3A1A',     // FARMLAND
  63: '#3A2A0A',     // FARMLAND_WET
  64: '#5A3A1A',     // JUNGLE_LOG
  65: '#1A5A1A',     // JUNGLE_LEAVES
  66: '#8B6B3E',     // ACACIA_LOG
  67: '#5A8B2E',     // ACACIA_LEAVES
  68: '#2A5A2A',     // VINE
  69: '#2A8A2A',     // LILY_PAD
};

export class Minimap {
  constructor(world) {
    this.world = world;
    this.isVisible = false;
    this._markers = []; // { x, z, color, label }

    // Canvas
    this.canvas = document.createElement('canvas');
    this.canvas.width = MAP_SIZE;
    this.canvas.height = MAP_SIZE;
    this.canvas.style.cssText = `
      position: fixed; top: 10px; right: 10px;
      width: ${MAP_SIZE}px; height: ${MAP_SIZE}px;
      border: 3px solid rgba(0,0,0,0.6);
      border-radius: 4px;
      image-rendering: pixelated;
      z-index: 100;
      display: none;
      pointer-events: none;
      box-shadow: 2px 2px 8px rgba(0,0,0,0.4);
    `;
    document.body.appendChild(this.canvas);

    this.ctx = this.canvas.getContext('2d');
    this._lastUpdate = 0;
  }

  toggle() {
    this.isVisible = !this.isVisible;
    this.canvas.style.display = this.isVisible ? 'block' : 'none';
  }

  addMarker(x, z, color, label) {
    this._markers.push({ x, z, color, label });
  }

  update(dt, playerPos, playerYaw, entities) {
    if (!this.isVisible) return;

    this._lastUpdate += dt;
    if (this._lastUpdate < 0.5) return; // Update every 0.5s
    this._lastUpdate = 0;

    const ctx = this.ctx;
    const px = Math.floor(playerPos.x);
    const pz = Math.floor(playerPos.z);

    // Clear
    ctx.fillStyle = '#222';
    ctx.fillRect(0, 0, MAP_SIZE, MAP_SIZE);

    // Draw terrain
    for (let dz = -MAP_RADIUS; dz < MAP_RADIUS; dz++) {
      for (let dx = -MAP_RADIUS; dx < MAP_RADIUS; dx++) {
        const wx = px + dx;
        const wz = pz + dz;

        // Find top block
        const topY = this.world.getTopBlock(wx, wz);
        if (topY <= 0) continue;

        const blockId = this.world.getBlock(wx, topY, wz);
        const color = BLOCK_MAP_COLORS[blockId];
        if (!color) continue;

        // Height shading
        const heightFactor = Math.max(0.5, Math.min(1.2, topY / 50));

        const sx = (dx + MAP_RADIUS) * PIXEL_SIZE;
        const sy = (dz + MAP_RADIUS) * PIXEL_SIZE;

        ctx.fillStyle = color;
        ctx.globalAlpha = heightFactor;
        ctx.fillRect(sx, sy, Math.ceil(PIXEL_SIZE), Math.ceil(PIXEL_SIZE));
      }
    }

    ctx.globalAlpha = 1;

    // Draw markers
    for (const m of this._markers) {
      const mdx = m.x - px;
      const mdz = m.z - pz;
      if (Math.abs(mdx) > MAP_RADIUS || Math.abs(mdz) > MAP_RADIUS) continue;

      const mx = (mdx + MAP_RADIUS) * PIXEL_SIZE;
      const my = (mdz + MAP_RADIUS) * PIXEL_SIZE;

      ctx.fillStyle = m.color;
      ctx.beginPath();
      ctx.arc(mx, my, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1;
      ctx.stroke();

      if (m.label) {
        ctx.fillStyle = '#FFF';
        ctx.font = '8px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(m.label, mx, my - 6);
      }
    }

    // Draw entities as colored dots
    if (entities && Array.isArray(entities)) {
      for (const e of entities) {
        if (!e.position) continue;
        const dx = e.position.x - px;
        const dz = e.position.z - pz;
        if (Math.abs(dx) >= MAP_RADIUS || Math.abs(dz) >= MAP_RADIUS) continue;
        const ex = MAP_SIZE / 2 + dx;
        const ez = MAP_SIZE / 2 + dz;
        // Color by type: red for hostile, green for passive, yellow for villager
        let color = '#44FF44';
        if (e.type === 'ZOMBIE' || e.type === 'SKELETON' || e.type === 'CREEPER' ||
            e.type === 'SPIDER' || e.type === 'ENDERMAN' || e.type === 'WITCH' ||
            e.type === 'PHANTOM' || e.type === 'DROWNED' || e.type === 'PILLAGER' ||
            e.type === 'WARDEN') color = '#FF4444';
        else if (e.type === 'VILLAGER') color = '#FFCC44';
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(ex, ez, 2, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Draw player (center arrow)
    const cx = MAP_SIZE / 2;
    const cy = MAP_SIZE / 2;
    const angle = playerYaw || 0;

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(-angle);

    // Arrow pointing up
    ctx.fillStyle = '#FF4444';
    ctx.beginPath();
    ctx.moveTo(0, -5);
    ctx.lineTo(-3, 4);
    ctx.lineTo(0, 2);
    ctx.lineTo(3, 4);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.restore();

    // North indicator
    ctx.fillStyle = '#FFF';
    ctx.font = 'bold 10px monospace';
    ctx.textAlign = 'center';
    ctx.fillText('N', MAP_SIZE / 2, 12);
  }
}
